import type { Tunables } from '../sim/tunables';

type Sample = 'crowd' | 'cheer' | 'touchdown' | 'itsGood';
const SAMPLES: readonly Sample[] = ['crowd', 'cheer', 'touchdown', 'itsGood'];

/**
 * The crowd and the stadium announcer are recorded-style audio (short files: a looping murmur, a cheer, and
 * the announcer's "touchdown" and "it's good" calls). Every other
 * sound is synthesised with Web Audio at runtime. Until the files arrive, or if they never do, a filtered
 * noise stands in for the crowd.
 * The context starts on the first tap (browsers block audio until then).
 */
export class GameAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private crowdGain: GainNode | null = null;
  /** Opens as the crowd gets louder, so a roar is brighter than a murmur, not just louder. */
  private crowdTone: BiquadFilterNode | null = null;
  private noiseBed: { src: AudioBufferSourceNode; gain: GainNode } | null = null;
  private noise: AudioBuffer | null = null;
  private readonly files: Partial<Record<Sample, Promise<ArrayBuffer | null>>> = {};
  private readonly samples: Partial<Record<Sample, AudioBuffer>> = {};
  private muted = false;

  constructor(private readonly t: Tunables) {
    // Start downloading now so the files are ready by the first tap; they are decoded once the context exists.
    const urls = t.audio.samples;
    for (const k of SAMPLES) {
      this.files[k] = fetch(urls[k])
        .then((r) => (r.ok ? r.arrayBuffer() : null))
        .catch(() => null);
    }
  }

  /** Call from a user gesture. */
  unlock(): void {
    if (this.ctx) {
      void this.ctx.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this.t.audio.master;
    this.master.connect(ctx.destination);
    // Two seconds of noise, reused by every noisy sound.
    const len = ctx.sampleRate * 2;
    this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    let b = 0;
    for (let i = 0; i < len; i++) {
      // Slightly pink noise sounds more like a crowd than white noise.
      b = 0.97 * b + 0.03 * (Math.random() * 2 - 1);
      d[i] = b * 6 + (Math.random() * 2 - 1) * 0.15;
    }
    this.startCrowd();
    for (const k of SAMPLES) void this.decode(k);
  }

  private async decode(k: Sample): Promise<void> {
    const data = await this.files[k];
    if (!data || !this.ctx) return;
    try {
      // decodeAudioData takes ownership of the bytes; the copy keeps a second unlock (or a test) safe.
      this.samples[k] = await this.ctx.decodeAudioData(data.slice(0));
    } catch {
      return;
    }
    if (k === 'crowd') this.swapCrowdBed();
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : this.t.audio.master;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  private startCrowd(): void {
    const ctx = this.ctx!;
    this.crowdGain = ctx.createGain();
    this.crowdGain.gain.value = this.t.audio.crowdBase;
    this.crowdGain.connect(this.master!);
    // The stand-in: band-passed noise, until the recorded crowd is decoded.
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = this.t.audio.crowdFilterHz;
    bp.Q.value = 0.6;
    const gain = ctx.createGain();
    src.connect(bp).connect(gain).connect(this.crowdGain);
    src.start();
    this.noiseBed = { src, gain };
  }

  /** Fades the recorded crowd in over the noise, then stops the noise. */
  private swapCrowdBed(): void {
    const ctx = this.ctx;
    const buf = this.samples.crowd;
    if (!ctx || !buf || !this.crowdGain || !this.noiseBed) return;
    const a = this.t.audio;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const [start, end] = audibleSpan(buf, a.loopTrimThreshold);
    src.loopStart = start;
    src.loopEnd = end;
    this.crowdTone = ctx.createBiquadFilter();
    this.crowdTone.type = 'lowpass';
    this.crowdTone.frequency.value = a.crowdMuffledHz;
    const gain = ctx.createGain();
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(a.crowdSampleGain, now + a.crowdSwapSeconds);
    src.connect(this.crowdTone).connect(gain).connect(this.crowdGain);
    src.start(now, start);
    const old = this.noiseBed;
    old.gain.gain.setValueAtTime(1, now);
    old.gain.gain.linearRampToValueAtTime(0, now + a.crowdSwapSeconds);
    old.src.stop(now + a.crowdSwapSeconds + 0.05);
    this.noiseBed = null;
  }

  /** 0 murmur to 1 roar, eased. */
  crowd(level: number, seconds = 0.6): void {
    if (!this.ctx || !this.crowdGain) return;
    const a = this.t.audio;
    const k = Math.max(0, Math.min(1, level));
    const g = a.crowdBase + (a.crowdCheer - a.crowdBase) * k;
    this.crowdGain.gain.setTargetAtTime(g, this.ctx.currentTime, seconds / 3);
    // Exponential between the two cut-offs: equal steps of excitement sound like equal steps of brightness.
    const hz = a.crowdMuffledHz * Math.pow(a.crowdOpenHz / a.crowdMuffledHz, k);
    this.crowdTone?.frequency.setTargetAtTime(hz, this.ctx.currentTime, seconds / 3);
  }

  /** One recorded sound (a crowd reaction or an announcer call), on top of the murmur. False until the file is decoded. */
  private reaction(k: Exclude<Sample, 'crowd'>, gain: number): boolean {
    const buf = this.samples[k];
    if (!buf || !this.ctx || !this.master) return false;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    src.connect(g).connect(this.master);
    src.start();
    return true;
  }

  private env(gain: number, attack: number, decay: number, at = 0): GainNode | null {
    if (!this.ctx || !this.master) return null;
    const g = this.ctx.createGain();
    const t0 = this.ctx.currentTime + at;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
    g.connect(this.master);
    return g;
  }

  private noiseHit(gain: number, freq: number, q: number, decay: number, type: BiquadFilterType = 'lowpass', at = 0): void {
    const g = this.env(gain, 0.004, decay, at);
    if (!g || !this.ctx) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    const f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    src.connect(f).connect(g);
    const t0 = this.ctx.currentTime + at;
    src.start(t0, Math.random());
    src.stop(t0 + decay + 0.05);
  }

  private tone(type: OscillatorType, freq: number, gain: number, attack: number, decay: number, at = 0, slideTo?: number): void {
    const g = this.env(gain, attack, decay, at);
    if (!g || !this.ctx) return;
    const o = this.ctx.createOscillator();
    o.type = type;
    const t0 = this.ctx.currentTime + at;
    o.frequency.setValueAtTime(freq, t0);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t0 + attack + decay);
    o.connect(g);
    o.start(t0);
    o.stop(t0 + attack + decay + 0.05);
  }

  /** at: seconds from now (the whistle comes a beat after the hit). */
  whistle(at = 0): void {
    const w = this.t.audio.whistleHz;
    this.tone('sine', w, 0.25, 0.01, 0.35, at);
    this.tone('sine', w * 1.04, 0.12, 0.01, 0.35, at);
  }

  snap(): void {
    this.noiseHit(0.5, 400, 1, 0.12);
    this.tone('triangle', 140, 0.4, 0.005, 0.1);
  }

  whoosh(): void {
    this.noiseHit(this.t.audio.whooshGain, 1800, 0.8, 0.35, 'bandpass');
  }

  catchThud(): void {
    this.noiseHit(this.t.audio.catchGain, 700, 1, 0.09);
    this.tone('sine', 180, 0.5, 0.003, 0.12, 0, 90);
  }

  /** Pads crack (a bright transient a phone speaker can play), a mid thump, then the low body. */
  hit(big = false): void {
    const a = this.t.audio;
    const k = big ? 1 : 0.7;
    this.noiseHit(a.crackGain * k, a.crackHz, 1.4, 0.05, 'bandpass');
    this.tone('triangle', big ? 240 : 280, a.thumpGain * k, 0.002, 0.09, 0, 120);
    this.noiseHit(a.hitGain * k, big ? 900 : 600, 0.7, big ? 0.28 : 0.18);
    this.tone('sine', big ? 90 : 120, a.hitGain * 0.8, 0.003, 0.22, 0, 45);
  }

  juke(): void {
    this.noiseHit(0.3, 2400, 1.2, 0.12, 'bandpass');
  }

  /** A different sound for each catch tap grade. */
  catchGrade(grade: 'perfect' | 'good' | 'miss' | 'none'): void {
    const a = this.t.audio;
    if (grade === 'perfect') {
      a.gradePerfectHz.forEach((f, i) => this.tone('sine', f, 0.3, 0.004, 0.18, i * 0.07));
    } else if (grade === 'good') {
      this.tone('square', a.gradeGoodHz, 0.06, 0.002, 0.04);
    } else {
      this.tone('sawtooth', a.gradeMissHz, 0.14, 0.004, 0.16, 0, a.gradeMissHz * 0.7);
    }
  }

  firstDown(): void {
    this.t.fx.firstDownChimeHz.forEach((f, i) => this.tone('triangle', f, 0.3, 0.004, 0.25, i * 0.09));
    this.crowd(0.8, 0.3);
    this.reaction('cheer', this.t.audio.cheerSmallGain);
  }

  ui(): void {
    this.tone('triangle', 660, 0.12, 0.003, 0.06);
  }

  incomplete(): void {
    this.tone('sawtooth', 200, 0.12, 0.01, 0.4, 0, 110);
    this.crowd(0.05, 0.3);
  }

  /** The announcer calls it over the roar; the synthesised horn stands in only if the call has not loaded. */
  touchdown(): void {
    this.score('touchdown');
  }

  fieldGoal(): void {
    this.score('itsGood');
  }

  /** The final whistle on a win: the crowd, no call. */
  win(): void {
    this.crowd(1, 0.3);
    this.reaction('cheer', this.t.audio.cheerGain);
  }

  private score(call: 'touchdown' | 'itsGood'): void {
    const a = this.t.audio;
    this.crowd(1, 0.3);
    if (this.reaction(call, a.announcerGain)) {
      this.reaction('cheer', a.cheerUnderCallGain);
      return;
    }
    this.horn();
    this.reaction('cheer', a.cheerGain);
  }

  private horn(): void {
    const a = this.t.audio;
    for (const [f, at] of [[262, 0], [330, 0.12], [392, 0.24], [523, 0.36]] as const) this.tone('sawtooth', f, a.hornGain, 0.02, 0.5, at);
    this.tone('sawtooth', 523, a.hornGain, 0.02, 1.2, 0.5);
    this.tone('sawtooth', 659, a.hornGain * 0.8, 0.02, 1.2, 0.5);
  }
}

/**
 * Seconds where the sound starts and stops, skipping near-silent samples at either end. MP3 encoders pad both
 * ends, and a loop over that padding clicks or gaps once a cycle.
 */
export function audibleSpan(buf: AudioBuffer, threshold: number): [number, number] {
  const n = buf.length;
  let first = n;
  let last = -1;
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c);
    let i = 0;
    while (i < first && Math.abs(d[i]!) < threshold) i++;
    first = Math.min(first, i);
    let j = n - 1;
    while (j > last && Math.abs(d[j]!) < threshold) j--;
    last = Math.max(last, j);
  }
  if (last <= first) return [0, buf.duration];
  return [first / buf.sampleRate, (last + 1) / buf.sampleRate];
}

/** Short vibration where supported (Android). iOS Safari ignores it. */
export function haptic(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Not supported: nothing to do.
  }
}
